import React, { useEffect, useMemo, useRef } from 'react';
import { bollinger, doubleBollinger } from '../../lib/crypto/indicators/mathB';

/**
 * BandFillOverlay — TradingView-style translucent fills between the
 * upper and lower bands of Bollinger / Double-Bollinger. Sits between
 * the price grid and the chart's line series so the fill hugs price
 * exactly. Repaints on every visible-range change (zoom/pan) and on
 * every candle mutation (live WS tick).
 *
 * Colours default to a dark-blue outer + amber inner scheme matching
 * the user's reference screenshot. Colours can be overridden per
 * pref via `indicator_colors.dbb_fill_outer` / `dbb_fill_inner` /
 * `bb_fill`.
 */
export default function BandFillOverlay({ engine, candles, indicators = {}, colors = null }) {
  const cnvRef = useRef(null);
  const wrapRef = useRef(null);

  // Compute band series once per candle-set change. `useMemo` is
  // cheap here: doubleBollinger is O(n) and n <= 1000.
  const bands = useMemo(() => {
    if (!candles?.length) return { dbb: null, bb: null };
    const dbb = indicators.dbb ? doubleBollinger(candles, 20, 1, 2) : null;
    const bb = (indicators.bb && !indicators.dbb) ? bollinger(candles, 20, 2) : null;
    return { dbb, bb };
  }, [candles, indicators.dbb, indicators.bb]);

  useEffect(() => {
    const enabled = !!(indicators.dbb || indicators.bb);
    if (!enabled || !engine?.provider?.chart) return;
    const chart = engine.provider.chart;

    const draw = () => {
      const cnv = cnvRef.current;
      const wrap = wrapRef.current;
      if (!cnv || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      cnv.width = rect.width * dpr;
      cnv.height = rect.height * dpr;
      cnv.style.width = `${rect.width}px`;
      cnv.style.height = `${rect.height}px`;
      const ctx = cnv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);
      const t2x = (t) => engine.provider.timeToX(t);
      const p2y = (p) => engine.provider.priceToY(p);

      const fillBand = (upperRows, lowerRows, color) => {
        if (!upperRows?.length || !lowerRows?.length) return;
        // Walk both series in parallel by time. They share the same
        // start offset so a linear zip works — but we sanity-check
        // by matching time stamps.
        ctx.beginPath();
        // Upper edge left→right
        let started = false;
        for (let i = 0; i < upperRows.length; i++) {
          const u = upperRows[i];
          const x = t2x(u.time); const y = p2y(u.value);
          if (x == null || y == null) continue;
          if (!started) { ctx.moveTo(x, y); started = true; }
          else ctx.lineTo(x, y);
        }
        // Lower edge right→left
        for (let i = lowerRows.length - 1; i >= 0; i--) {
          const l = lowerRows[i];
          const x = t2x(l.time); const y = p2y(l.value);
          if (x == null || y == null) continue;
          ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
      };

      // Double Bollinger — outer (2σ) then inner (1σ) so the inner
      // fill visually sits on top and reads as a brighter core zone.
      if (bands.dbb) {
        fillBand(bands.dbb.upper2, bands.dbb.lower2, (colors?.dbb_fill_outer) || 'rgba(60,80,220,0.22)');
        fillBand(bands.dbb.upper1, bands.dbb.lower1, (colors?.dbb_fill_inner) || 'rgba(247,150,50,0.20)');
      } else if (bands.bb) {
        fillBand(bands.bb.upper, bands.bb.lower, (colors?.bb_fill) || 'rgba(0,229,255,0.10)');
      }
    };
    draw();
    chart.timeScale().subscribeVisibleTimeRangeChange(draw);
    const obs = new ResizeObserver(draw);
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => {
      try { chart.timeScale().unsubscribeVisibleTimeRangeChange(draw); } catch { /* noop */ }
      obs.disconnect();
    };
  }, [engine, bands, indicators.dbb, indicators.bb, colors]);

  if (!(indicators.dbb || indicators.bb)) return null;
  return (
    <div ref={wrapRef} data-testid="crypto-bandfill-overlay" className="absolute inset-0 z-[5] pointer-events-none">
      <canvas ref={cnvRef} />
    </div>
  );
}
